import {
  createAction,
  createAsyncThunk,
  createEntityAdapter,
  EntityState,
  PayloadAction,
  Update,
} from '@reduxjs/toolkit';
import { DashboardReduxWidget, UpdateWidgetActiveStatePayload, UpdateWidgetSectionPayload } from './types';
import { DashboardAppState, selectDashboardsAppState } from './dashboards.app.reducer';
import { DashboardsReduxActions } from '../types/types';
import {
  DashboardType,
  SetWidgetCategoryStatusPayload,
  SetWidgetCategoryStatusResponse,
  SmartViewWidget,
  ViewType,
  WidgetCategory,
  WidgetCategoryConfig,
} from '@nirvana/shared/dtos/types/generic-dashboards/gz-console';
import {
  checkIfHasUnsavedChanges,
  selectActiveViewConfiguration,
  selectDefaultView,
  smartViewsAdapter,
} from './dashboards.smart.views.slice';
import { isEqual } from 'lodash';
import { RootState } from '../../redux/store';
import { dashboardConfigurationSelector } from './dashboards.ui.slice';
import { MicroserviceRequest } from '../../utils/request/microservice.request';
import { GENERIC_DASHBOARD_MICROSERVICE_CLIENT_NAME, GenericDashboardApiMethods } from '../types/constants';
import { getTranslatedWidgetTitle } from '../utils/translations';
import { DashboardSmartView } from '../gzDashboardSmartViewsList/types';

export const widgetsAdapter = createEntityAdapter<DashboardReduxWidget>({
  selectId: (widget: DashboardReduxWidget) => widget.id ?? '',
});

export const widgetCategoryEntityAdapter = createEntityAdapter<WidgetCategoryConfig>({
  selectId: (widgetCategory: WidgetCategoryConfig) => widgetCategory.id ?? '',
});

export interface WidgetsSliceState {
  widgets: EntityState<DashboardReduxWidget>;
  categories: EntityState<WidgetCategoryConfig>;
  searchText: string;
  refreshNecessary: boolean;
}

export const widgetsInitialState: WidgetsSliceState = {
  widgets: widgetsAdapter.getInitialState(),
  categories: widgetCategoryEntityAdapter.getInitialState(),
  searchText: '',
  refreshNecessary: false,
};

/**
 * This function should only be used when finding that a user has a custom view with a widget that we
 * have split into other widgets. We need a redux widget config in order to render the widget
 * properly inside the GridStackManagerComponent.
 * @param smartViewWidget
 */
export const getDefaultConfigForChangedWidget = (smartViewWidget: SmartViewWidget): DashboardReduxWidget => {
  return {
    id: smartViewWidget.id,
    isActive: false, // this bool will be set properly in another function
    isSelected: false, // this bool will be set properly in another function
    activeLayout: smartViewWidget.defaultLayout, // active layout should be the saved layout from the view
    category: WidgetCategory.CATEGORY_ZERO, // does not matter
    config: {
      defaultSize: smartViewWidget.defaultLayout.size, // does not matter
      resizeBreakingPoints: [], // does not matter
      resizable: false,
      movable: false,
      closable: true,
      locked: true,
      forbidden: true, // MUST be true to avoid adding the widget from settings panel
    },
  };
};

// SELECTORS
export const widgetsSelector = widgetsAdapter.getSelectors<DashboardAppState>(
  (state: DashboardAppState) => state.widgetsSlice.widgets
);

export const widgetsCategoriesSelector = widgetCategoryEntityAdapter.getSelectors<DashboardAppState>(
  (state: DashboardAppState) => state.widgetsSlice.categories
);

export const selectWidgetsSearchText = (state: DashboardAppState): string => state.widgetsSlice.searchText;
export const selectWidgets = (state: DashboardAppState): DashboardReduxWidget[] => widgetsSelector.selectAll(state);
export const selectActiveWidgets = (state: DashboardAppState): DashboardReduxWidget[] =>
  widgetsSelector.selectAll(state).filter(widget => widget.isActive);

export const selectInactiveWidgets = (state: DashboardAppState): DashboardReduxWidget[] =>
  widgetsSelector.selectAll(state).filter(widget => !widget.isActive);

export const selectRefreshNecessary = (state: DashboardAppState): boolean => state.widgetsSlice.refreshNecessary;

export const selectFilteredWidgets = (state: DashboardAppState): Array<DashboardReduxWidget> => {
  const searchText = selectWidgetsSearchText(state);

  return widgetsSelector
    .selectAll(state)
    .filter(
      (widget: DashboardReduxWidget) =>
        getTranslatedWidgetTitle(widget.id).toLowerCase().includes(searchText) && !widget.config.forbidden
    );
};

export const selectFilteredWidgetsSections = (state: DashboardAppState): Array<WidgetCategoryConfig> => {
  const validWidgets: Array<DashboardReduxWidget> = selectFilteredWidgets(state);

  return widgetsCategoriesSelector.selectAll(state).filter((section: WidgetCategoryConfig) => {
    for (const widget of validWidgets) {
      if (section.category === widget.category) {
        return true;
      }
    }

    return false;
  });
};

export const selectWidgetCategories = (state: DashboardAppState): Array<WidgetCategoryConfig> => {
  return widgetsCategoriesSelector.selectAll(state);
};

export const hasForbiddenWidgets = (state: DashboardAppState): boolean => {
  const forbiddenWidgets = selectWidgets(state).filter(widget => widget.config.forbidden);
  return forbiddenWidgets.length !== 0;
};

// HELPERS
export const updateWidgetsConfigsOnState = (state: DashboardAppState, payload: Array<Update<DashboardReduxWidget>>) => {
  const activeView = selectActiveViewConfiguration(state);
  const isPartner = Application.Main.company.isPartner();
  if (
    !isPartner &&
    hasForbiddenWidgets(state) &&
    state.viewsSlice.gridStackFirstInitOfWidgets &&
    activeView.type === ViewType.DEFAULT
  ) {
    const smartViewWidgets = activeView.widgets;
    const newWidgetsConfigs: Array<SmartViewWidget> = [];

    const updatedWidgetsIds: Array<number | string> = payload.map(widget => widget.id);

    for (const smartViewWidget of smartViewWidgets) {
      if (updatedWidgetsIds.includes(smartViewWidget.id)) {
        const updatedDefaultLayout = payload.find(widget => widget.id === smartViewWidget.id)?.changes.activeLayout;
        if (!updatedDefaultLayout) {
          throw new Error('Could not find active layout of widget.');
        }

        newWidgetsConfigs.push({
          ...smartViewWidget,
          defaultLayout: updatedDefaultLayout,
        });
        continue;
      }

      newWidgetsConfigs.push(smartViewWidget);
    }

    const updateObj: Update<DashboardSmartView> = {
      id: activeView.id,
      changes: {
        widgets: newWidgetsConfigs,
      },
    };

    smartViewsAdapter.updateOne(state.viewsSlice.views, updateObj);
  }

  state.viewsSlice.gridStackFirstInitOfWidgets = false;

  widgetsAdapter.updateMany(state.widgetsSlice.widgets, payload);
  state.viewsSlice.hasUnsavedChanges = checkIfHasUnsavedChanges(state);
};

export const checkIfWidgetsChanged = (state: DashboardAppState) => {
  const activeWidgets = selectActiveWidgets(state);
  const viewWidgets = selectActiveViewConfiguration(state).widgets;

  if (activeWidgets.length !== viewWidgets.length) {
    return true;
  }

  const activeWidgetKeys = activeWidgets.map(widget => widget.id);
  const viewWidgetKeys = viewWidgets.map(widget => widget.id);

  if (!isEqual(activeWidgetKeys.sort(), viewWidgetKeys.sort())) {
    return true;
  }

  for (const widget of activeWidgets) {
    const viewWidget = viewWidgets.find(viewWidgetConfig => viewWidgetConfig.id === widget.id);
    if (!viewWidget) {
      throw new Error('Could not find view widget.');
    }

    if (!isEqual(widget.activeLayout, viewWidget.defaultLayout)) {
      return true;
    }
  }

  return false;
};

export const updateWidgetsSearchText = createAction<string>(DashboardsReduxActions.UPDATE_WIDGETS_SEARCH_TEXT);

export const updateWidgetsSearchTextActionHandler = (state: DashboardAppState, action: PayloadAction<string>): void => {
  state.widgetsSlice.searchText = action.payload;
};

export const setRefreshWidgets = createAction<boolean>(DashboardsReduxActions.SET_REFRESH_WIDGETS);

export const setRefreshWidgetsActionHandler = (state: DashboardAppState, action: PayloadAction<boolean>): void => {
  state.widgetsSlice.refreshNecessary = action.payload;
};

export const toggleIsOpenStateForWidgetCategory = createAsyncThunk<WidgetCategoryConfig, UpdateWidgetSectionPayload>(
  DashboardsReduxActions.TOGGLE_COLLAPSED_STATE_FOR_WIDGETS_CATEGORY,
  async (payload: UpdateWidgetSectionPayload, { getState }): Promise<WidgetCategoryConfig> => {
    const rootState: RootState = getState() as RootState;
    const dashboardAppState: DashboardAppState = selectDashboardsAppState(rootState);
    const dashboardType: DashboardType = dashboardConfigurationSelector(dashboardAppState).dashboardType;

    const request: SetWidgetCategoryStatusPayload = {
      dashboardType: dashboardType,
      categoryId: payload.widgetCategory.id,
      isWidgetCategoryOpen: payload.isOpen,
    };

    await MicroserviceRequest.makeRequest<SetWidgetCategoryStatusPayload, SetWidgetCategoryStatusResponse>(
      GENERIC_DASHBOARD_MICROSERVICE_CLIENT_NAME,
      GenericDashboardApiMethods.SET_WIDGET_CATEGORY_STATUS,
      request
    );

    return {
      ...payload.widgetCategory,
      isOpen: payload.isOpen,
    };
  }
);

export const toggleIsOpenStateForWidgetCategoryFulfilledActionHandler = (
  state: DashboardAppState,
  action: PayloadAction<WidgetCategoryConfig>
): void => {
  widgetCategoryEntityAdapter.upsertOne(state.widgetsSlice.categories, action.payload);
};

// WIDGETS ACTIONS
export const updateWidget = createAction<Update<DashboardReduxWidget>>(DashboardsReduxActions.UPDATE_WIDGET);
export const updateWidgetActionHandler = (
  state: DashboardAppState,
  action: PayloadAction<Update<DashboardReduxWidget>>
): void => {
  widgetsAdapter.updateOne(state.widgetsSlice.widgets, action.payload);
};

export const resetWidgets = createAction(DashboardsReduxActions.RESET_WIDGETS);
export const resetWidgetsActionHandler = (state: DashboardAppState) => {
  const availableWidgets = selectWidgets(state);
  const updateData: Array<Update<DashboardReduxWidget>> = [];

  let viewConfig: DashboardSmartView;
  if (state.uiSlice.configuration.smartViewsPanel.visible) {
    // dashboard with smart views
    viewConfig = selectActiveViewConfiguration(state);
  } else {
    // dashboard without smart views, so we need to get the default view configuration
    viewConfig = selectDefaultView(state);
  }

  for (const widget of availableWidgets) {
    const smartViewWidget = viewConfig.widgets.find(widgetConfig => widgetConfig.id === widget.id);
    if (smartViewWidget) {
      updateData.push({
        id: widget.id,
        changes: {
          isActive: true,
          isSelected: true,
          activeLayout: smartViewWidget.defaultLayout,
        },
      });
      continue;
    }

    updateData.push({
      id: widget.id,
      changes: {
        isActive: false,
        isSelected: false,
        activeLayout: undefined,
      },
    });
  }

  updateWidgetsConfigsOnState(state, updateData);
  state.viewsSlice.gridStackResetNeeded = true;
};

export const updateWidgetsConfigs = createAction<ReadonlyArray<Update<DashboardReduxWidget>>>(
  DashboardsReduxActions.UPDATE_WIDGETS_CONFIGS
);
export const updateWidgetsConfigsActionHandler = (
  state: DashboardAppState,
  action: PayloadAction<Array<Update<DashboardReduxWidget>>>
): void => {
  updateWidgetsConfigsOnState(state, action.payload);
};

export const markWidgetInactive = createAction<string>(DashboardsReduxActions.MARK_WIDGET_INACTIVE);

export const getToggleWidgetActiveStateUpdate = (
  state: DashboardAppState,
  widgetId: string,
  options: {
    isActive: boolean,
    isSelected: boolean,
  }
): Update<DashboardReduxWidget> => {
  const widget = widgetsAdapter.getSelectors().selectById(state.widgetsSlice.widgets, widgetId);

  if (!widget) {
    throw new Error(`Widget entity ${widgetId} not found`);
  }

  return {
    id: widgetId,
    changes: {
      ...widget,
      ...options
    },
  };
};

export const markWidgetInactiveActionHandler = (state: DashboardAppState, action: PayloadAction<string>): void => {
  const update = getToggleWidgetActiveStateUpdate(state, action.payload, {isActive: false, isSelected: false});
  widgetsAdapter.updateOne(state.widgetsSlice.widgets, update);
  state.viewsSlice.hasUnsavedChanges = checkIfHasUnsavedChanges(state);
};

export const updateSettingsPanelWidgetsActive2State = createAction<Array<UpdateWidgetActiveStatePayload>>(
  DashboardsReduxActions.UPDATE_SETTINGS_PANEL_WIDGETS_ACTIVE2_STATE
);
export const updateSettingsPanelWidgetsActive2StateActionHandler = (
  state: DashboardAppState,
  action: PayloadAction<Array<UpdateWidgetActiveStatePayload>>
): void => {
  const updates = action.payload.map((payload: UpdateWidgetActiveStatePayload) =>
    getToggleWidgetActiveStateUpdate(state, payload.widgetId, { isActive: payload.isActive, isSelected: payload.isSelected })
  );

  state.viewsSlice.gridStackFirstInitOfWidgets = false;

  widgetsAdapter.updateMany(state.widgetsSlice.widgets, updates);
  state.viewsSlice.hasUnsavedChanges = checkIfHasUnsavedChanges(state);
};

export const setWidgets = createAction<Array<DashboardReduxWidget>>(DashboardsReduxActions.SET_WIDGETS);
export const setWidgetsActionHandler = (
  state: DashboardAppState,
  action: PayloadAction<Array<DashboardReduxWidget>>
) => {
  widgetsAdapter.setAll(state.widgetsSlice.widgets, action.payload);
};

export const setWidgetsCategories = createAction<Array<WidgetCategoryConfig>>(
  DashboardsReduxActions.SET_WIDGETS_CATEGORIES
);
export const setWidgetsCategoriesActionHandler = (
  state: DashboardAppState,
  action: PayloadAction<Array<WidgetCategoryConfig>>
) => {
  widgetCategoryEntityAdapter.setAll(state.widgetsSlice.categories, action.payload);
};
